import React, { useMemo } from "react";
import type { Project } from "../../../../api/useProjects";
import { useProject } from "../../../../api/useProjects";
import { css } from "@linaria/core";
import { colors, spacings } from "@ui/Theme";
import { styled } from "@linaria/react";
import Link from "@ui/Link";
import { ArrowRightUpLine } from "@ui/IconV2/react";
import t from "../../../../i18n";
import Text from "@ui/Text";
import classNames from "classnames";
import PriorityTable from "./PriorityTable";
import { useComments } from "../../../../api/useComments";
import { useQuantities, type Quantity } from "../../../../api/useQuantities";
import useSparkline from "../../Events/hooks/charts/sparkline/useSparkline";
import useActiveAnalysesStream from "../../../../api/useActiveAnalysesStream";
import {
  aggregationIntervalFromPeriod,
  mapIntervalFromEntities,
  type AggregationPeriod,
} from "../../../../domain/aggregation/period";
import type { PriorityFaultEvent } from "diagnostics-domain/faultEvent";
import { useIndexedFailureValues } from "../../../../api/useIndexedFailureValues";
import { findPowerQuantityId } from "diagnostics-domain/aggregationPeriod";
import { fonts } from "@ui";
import { useSettings } from "../../../../api/useSettings";
import { toPath } from "../../../../router/helpers/path";
import { useEvents, type Event } from "../../../../api/useEvents";
import { getEventPriority, getEventSystems } from "../../../../domain/event";
import { toBasenamePath, useBasename } from "../../../../router/helpers/navigation";
import useGroups from "../../../../api/useGroups";
import { Tag } from "@ui/Tag";
import FilterList from "@ui/Filter/FilterList";

const PRIORITY_ORDER: Record<Exclude<PriorityFaultEvent, null>, number> = {
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
};

export function isDateWithinAggregationPeriod(
  date: number,
  aggregationPeriod: { from: Date; to: Date },
): boolean {
  const fromTime = aggregationPeriod.from.getTime();
  const toTime = aggregationPeriod.to.getTime();
  return date >= fromTime && date <= toTime;
}

interface PriorityEventWidgetProps {
  projectId: string;
  className?: string;
  systemIds: string[];
}

const PriorityEventWidget = ({ projectId, className, systemIds }: PriorityEventWidgetProps) => {
  const settings = useSettings();
  const manualEventEnabled = useMemo(() => settings.MANUAL_EVENTS_DEV_MODE_ENABLED, [settings]);
  const settingsAggregationPeriod = settings["AGGREGATION_PERIOD"] as AggregationPeriod;
  // Fetch all required data using hooks
  const events = useEvents(projectId, manualEventEnabled);
  const indexedFailureValues = useIndexedFailureValues(projectId);
  const comments = useComments(projectId);
  const quantities = useQuantities(projectId);
  const { activeAnalyses, isStreaming: isActiveAnalysesStreaming } =
    useActiveAnalysesStream(projectId);
  const project = useProject(projectId);
  if (!project) {
    throw new Error("invalid project");
  }

  // Get performance indicator (first QOI quantity)
  const performanceIndicator = quantities.filter((q: Quantity) => q.qoiOrder !== null)?.[0] ?? null;

  // Calculate aggregation period based on duration
  const groups = useGroups(project.id);
  const aggregationPeriod = useMemo(() => {
    if (settingsAggregationPeriod === "CYCLE" || settingsAggregationPeriod === "CYCLE_2") {
      return aggregationIntervalFromPeriod({
        period: settingsAggregationPeriod,
        timezone: project.timezone,
        intervals: mapIntervalFromEntities(groups),
      });
    } else if (settingsAggregationPeriod === "ALL") {
      return aggregationIntervalFromPeriod({
        period: settingsAggregationPeriod,
        timezone: project.timezone,
        limit: {
          from: new Date(project.analysesAggregate?.earliestActiveAnalysis?.date ?? Date.now()),
          to: new Date(),
        },
      });
    } else {
      return aggregationIntervalFromPeriod({
        period: settingsAggregationPeriod,
        timezone: project.timezone,
      });
    }
  }, [groups, settingsAggregationPeriod, project]);

  // Find power quantity ID
  const powerQuantityId = useMemo(() => findPowerQuantityId(quantities), [quantities]);

  // Filter and sort fault events
  const filteredEvents = useMemo(() => {
    if (!project) return [];

    const isInProgress = (event: Event) => event.metadata.status !== "CLOSED";
    const isActive = (event: Event) => event.metadata.active;
    const hasPriority = (event: Event) => ["HIGH", "MEDIUM"].includes(getEventPriority(event));
    const isMatchingSystem = (event: Event) => {
      if (systemIds.length === 0) return true;
      return getEventSystems(event).some(systemId => systemIds.includes(systemId));
    };
    const hasNonZeroPowerImpactsForEvent = (event: Event, powerQuantityId: string | null) => {
      if (powerQuantityId === null) return false;

      switch (event.type) {
        case "DIAGNOSED": {
          return event.metadata.faults.some(fault =>
            fault.statusByAnalysis.some(faultStatus => {
              if (!isDateWithinAggregationPeriod(faultStatus.date, aggregationPeriod)) return false;
              const faultValue = indexedFailureValues[faultStatus.date]?.[fault.name];
              if (faultValue === undefined || faultValue === null) return false;

              const powerImpact = faultValue.impacts[powerQuantityId];
              if (powerImpact === undefined) return false;

              return powerImpact !== 0;
            }),
          );
        }
        case "MANUAL":
          return event.impacts.some(
            impact => impact.quantityId === powerQuantityId && impact.value !== 0,
          );
        default:
          return false;
      }
    };

    return events
      .filter(
        event =>
          isInProgress(event) &&
          isActive(event) &&
          hasPriority(event) &&
          isMatchingSystem(event) &&
          hasNonZeroPowerImpactsForEvent(event, powerQuantityId),
      )
      .sort((a, b) => {
        const getPriority = (event: Event) => PRIORITY_ORDER[getEventPriority(event)] ?? 3;
        const priorityDifference = getPriority(a) - getPriority(b);

        if (priorityDifference !== 0) return priorityDifference;
        return a.name.localeCompare(b.name);
      });
  }, [events, indexedFailureValues, powerQuantityId, aggregationPeriod, project, systemIds]);

  // Generate sparkline data
  const { sparklineStatisticByEventId, sparklineOptionsByEventId } = useSparkline({
    project: project ?? ({} as Project), // Provide fallback
    performanceIndicator: performanceIndicator ?? null,
    aggregationPeriod,
    activeAnalyses,
    events: filteredEvents,
  });

  const basename = useBasename();

  // If project is not found, don't render anything
  if (!project) {
    return null;
  }

  return (
    <div className={classNames(className)}>
      <div className={blockPriorityHeader}>
        <div className={blockPriorityTitle}>
          <Text type="body1SemiBold">{t("project.plant_state.block.priority_event.title")}</Text>
          <StyledLink
            to={toBasenamePath(
              toPath("/projects/:projectId/fault-events", {
                projectId: project.id,
              }),
              basename ?? "/",
            )}
            target="_blank"
          >
            <ArrowRightUpLine size="s" />
            {t("project.plant_state.block.priority_event.button_link")}
          </StyledLink>
        </div>
      </div>
      {systemIds.length > 0 && (
        <div className={blockPriorityFilters}>
          <Text type="body1">
            {t("project.fault_events.table.live.metadata.system.multiple", systemIds.length)}
          </Text>
          <FilterList
            className={blockPriorityFilterList}
            filters={systemIds.map(systemId => ({ id: systemId, label: systemId }))}
            renderFilter={filter => (
              <Tag className={blockPriorityFilterTag} size="s">
                <Text type="body2SemiBold">{filter.label}</Text>
              </Tag>
            )}
          />
        </div>
      )}

      {filteredEvents.length === 0 && (
        <Text className={blockPriorityNoFaultEvents}>
          {t("project.plant_state.block.priority_event.no_fault_event")}
        </Text>
      )}

      {filteredEvents.length > 0 && (
        <PriorityTable
          className={tablePriority}
          durationISO8601={settingsAggregationPeriod}
          project={project}
          performanceIndicator={performanceIndicator}
          events={filteredEvents}
          comments={comments}
          sparklineStatisticByEventId={sparklineStatisticByEventId}
          sparklineOptionsByEventId={sparklineOptionsByEventId}
          loading={isActiveAnalysesStreaming}
        />
      )}
    </div>
  );
};
export default PriorityEventWidget;

//#region CSS
const blockPriorityHeader = css`
  display: flex;
  align-items: center;
  gap: ${spacings.m};
  height: ${spacings.xl};
`;

const blockPriorityTitle = css`
  display: flex;
  align-items: baseline;
  gap: ${spacings.xs};
  color: ${colors.carbon800};
  height: ${spacings.xl};
  align-items: center;
`;
const blockPriorityFilters = css`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: ${spacings.xs};
`;

const blockPriorityFilterList = css`
  gap: ${spacings.xs};
`;

const blockPriorityFilterTag = css`
  padding: ${spacings.xxs} ${spacings.xs};
  background-color: ${colors.lightNitrogen50} !important;
  border-radius: ${spacings.xxs};
  border: 1px solid ${colors.lightNitrogen500_Main};
`;
const blockPriorityNoFaultEvents = css`
  margin-top: ${spacings.m};
  color: ${colors.carbon500};
`;
const StyledLink = styled(Link)`
  display: flex;
  align-items: baseline;
  color: ${colors.lightNitrogen500_Main};
  gap: ${spacings.xxs};
  font: ${fonts.body1};
  :hover {
    text-decoration: underline;
    color: ${colors.lightNitrogen600};
  }

  & > :nth-child(1) {
    align-self: center;
  }
`;
const tablePriority = css`
  margin-top: ${spacings.m};
`;
//#endregion CSS
